import Button from '@mui/material/Button';
import CommentIcon from '@mui/icons-material/Comment';
import { getMetaData } from '../../../tool';
import { Comment } from '../../../types';
import useCommentSectionStyles from './useCommentSectionStyles';
import useCommentSection from '../../../hooks/useCommentSection';
import useGlobalStyles from '../../useGlobalStyles';
import useInputStyles from '../baseComponents/input/useInputStyles';
import usePageLink from '../../../hooks/useQuestion';

/**
 * Interface representing the props for the Comment Section component.
 *
 * - comments - list of the comment components
 * - handleAddComment - a function that handles adding a new comment, taking a Comment object as an argument
 * - width - the width of the comment section
 */
interface CommentSectionProps {
  comments: Comment[];
  handleAddComment: (comment: Comment) => void;
  width?: string;
}

/**
 * CommentSection component shows the users all the comments and allows the users add more comments.
 *
 * @param comments: an array of Comment objects
 * @param handleAddComment: function to handle the addition of a new comment
 * @param width: the width of the comment section (default is 'none' which means auto)
 */
const CommentSection = ({ comments, handleAddComment, width = 'none' }: CommentSectionProps) => {
  const { classes } = useCommentSectionStyles();
  const { classes: inputClasses } = useInputStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const { handleUser } = usePageLink();

  const { showComments, setShowComments, text, setText, textErr, handleAddCommentClick } =
    useCommentSection(handleAddComment);

  return (
    <div className={classes.commentSection} style={{ width }}>
      <Button className={classes.toggleButton} onClick={() => setShowComments(!showComments)}>
        <CommentIcon />
        {showComments ? 'Hide Comments' : 'Show Comments'}
      </Button>

      {showComments && (
        <div className={classes.commentsContainer}>
          <ul className={classes.commentsList}>
            {comments.length > 0 ? (
              comments.map((comment, index) => (
                <li key={index} className={classes.commentItem}>
                  <p className={classes.commentText}>{comment.text}</p>
                  <small className={classes.commentMeta}>
                    <span
                      className={`${classes.commentAuthor} ${!comment.commentByUser && classes.deletedCommentAuthor}`}
                      onClick={() => {
                        if (comment.commentByUser) handleUser(comment.commentByUser?.username);
                      }}>
                      {comment.commentByUser?.username || 'Deleted User'}
                    </span>
                    {getMetaData(new Date(comment.commentDateTime))}
                  </small>
                </li>
              ))
            ) : (
              <p className={`${globalClasses.notExist} ${classes.noComments}`}>No comments yet.</p>
            )}
          </ul>

          <div className={classes.addComment}>
            <div className={classes.inputRow}>
              <textarea
                placeholder='Comment'
                value={text}
                onChange={e => setText(e.target.value)}
                className={`${globalClasses.input} ${inputClasses.inputInput} ${classes.commentTextarea}`}
              />
              <Button
                className={`${globalClasses.button} ${classes.addCommentButton}`}
                onClick={() => {
                  setTimeout(() => {
                    handleAddCommentClick();
                  }, 200);
                }}>
                Add Comment
              </Button>
            </div>
            {textErr && (
              <small className={`${globalClasses.error} ${classes.error}`}>{textErr}</small>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
